import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import confetti from 'canvas-confetti';
import { getWedding } from '../services/wedding';
import { getSuggestions, completeOnboarding, SuggestedTopic, OnboardingSubmitData } from '../services/onboarding';
import EventDetailsStep from '../components/onboarding/EventDetailsStep';
import PlannersStep from '../components/onboarding/PlannersStep';
import TopicSelectionStep from '../components/onboarding/TopicSelectionStep';
import TaskSelectionStep from '../components/onboarding/TaskSelectionStep';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';

const STEPS = ['Event Details', 'Planners', 'Topics', 'Tasks'];

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [mobileTopicIndex, setMobileTopicIndex] = useState(0);

  // Data
  const [suggestions, setSuggestions] = useState<SuggestedTopic[]>([]);
  const [weddingTitle, setWeddingTitle] = useState('');
  const [weddingDate, setWeddingDate] = useState('');
  const [weddingVenue, setWeddingVenue] = useState('');
  const [planners, setPlanners] = useState<{ name: string; color: string | null }[]>(
    user?.full_name ? [{ name: user.full_name, color: null }] : []
  );
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [selectedTasks, setSelectedTasks] = useState<Map<string, Set<string>>>(new Map());
  const [pendingPlannerName, setPendingPlannerName] = useState('');
  const [showPlannerPrompt, setShowPlannerPrompt] = useState(false);
  const addPendingPlannerRef = React.useRef<(() => void) | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        const [wRes, sRes] = await Promise.all([getWedding(), getSuggestions()]);
        if (wRes.data.onboarding_completed === true) {
          navigate('/dashboard', { replace: true });
          return;
        }
        setWeddingTitle(wRes.data.title);
        setSuggestions(sRes.data);
        // Select all topics and all tasks by default
        const allTopicNames = new Set(sRes.data.map((t: SuggestedTopic) => t.name));
        setSelectedTopics(allTopicNames);
        const allTasks = new Map<string, Set<string>>();
        sRes.data.forEach((t: SuggestedTopic) => {
          allTasks.set(t.name, new Set(t.tasks));
        });
        setSelectedTasks(allTasks);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]);

  const activeTopics = suggestions.filter((t) => selectedTopics.has(t.name));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const data: OnboardingSubmitData = {
        wedding_title: weddingTitle || null,
        wedding_date: weddingDate || null,
        wedding_venue: weddingVenue || null,
        planners,
        topics: activeTopics.map((t) => ({
          name: t.name,
          emoji: t.emoji,
          tasks: Array.from(selectedTasks.get(t.name) || []).map((title) => ({ title })),
        })),
      };
      await completeOnboarding(data);
      setCompleted(true);
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { x: 0.5, y: 0.4 },
        colors: ['#B07A8F', '#C9A96E', '#6B9E78', '#6A8EB0', '#D4A84E'],
        zIndex: 1400,
      });
      setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
    } catch (err) {
      console.error(err);
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 3) {
      // Task step
      if (isMobile && mobileTopicIndex < activeTopics.length - 1) {
        setMobileTopicIndex(mobileTopicIndex + 1);
      } else {
        handleSubmit();
      }
    } else if (activeStep === 2 && selectedTopics.size === 0) {
      // No topics selected — skip tasks step and submit
      handleSubmit();
    } else if (activeStep === 1 && pendingPlannerName.trim()) {
      // User typed a name but didn't click Add — prompt them
      setShowPlannerPrompt(true);
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 3 && isMobile && mobileTopicIndex > 0) {
      setMobileTopicIndex(mobileTopicIndex - 1);
    } else {
      setActiveStep(activeStep - 1);
      if (activeStep === 3) setMobileTopicIndex(0);
    }
  };

  const handleSkip = () => {
    if (activeStep === 1) {
      // Skip planners
      setPlanners([]);
      setActiveStep(2);
    } else if (activeStep === 2) {
      // Skip topics → submit immediately with nothing
      setSelectedTopics(new Set());
      // Need to call submit after state update — use callback pattern
      handleSubmitEmpty();
    } else if (activeStep === 3) {
      if (isMobile) {
        // Skip current topic's tasks
        const currentTopic = activeTopics[mobileTopicIndex];
        if (currentTopic) {
          const next = new Map(selectedTasks);
          next.set(currentTopic.name, new Set());
          setSelectedTasks(next);
        }
        if (mobileTopicIndex < activeTopics.length - 1) {
          setMobileTopicIndex(mobileTopicIndex + 1);
        } else {
          handleSubmit();
        }
      } else {
        // Desktop: skip all tasks
        const emptyTasks = new Map<string, Set<string>>();
        activeTopics.forEach((t) => emptyTasks.set(t.name, new Set()));
        setSelectedTasks(emptyTasks);
        handleSubmit();
      }
    }
  };

  const handleSubmitEmpty = async () => {
    setSubmitting(true);
    try {
      const data: OnboardingSubmitData = {
        wedding_title: weddingTitle || null,
        wedding_date: weddingDate || null,
        wedding_venue: weddingVenue || null,
        planners,
        topics: [],
      };
      await completeOnboarding(data);
      setCompleted(true);
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { x: 0.5, y: 0.4 },
        colors: ['#B07A8F', '#C9A96E', '#6B9E78', '#6A8EB0', '#D4A84E'],
        zIndex: 1400,
      });
      setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
    } catch (err) {
      console.error(err);
      setSubmitting(false);
    }
  };

  const handleEventChange = (field: 'title' | 'date' | 'venue', value: string) => {
    if (field === 'title') setWeddingTitle(value);
    else if (field === 'date') setWeddingDate(value);
    else setWeddingVenue(value);
  };

  // When topics change, ensure selectedTasks map stays in sync
  const handleTopicsChange = (newSelected: Set<string>) => {
    setSelectedTopics(newSelected);
    // Add tasks for newly selected topics (all checked by default)
    const next = new Map(selectedTasks);
    newSelected.forEach((name) => {
      if (!next.has(name)) {
        const topic = suggestions.find((t) => t.name === name);
        if (topic) next.set(name, new Set(topic.tasks));
      }
    });
    // Remove tasks for deselected topics
    Array.from(next.keys()).forEach((name) => {
      if (!newSelected.has(name)) next.delete(name);
    });
    setSelectedTasks(next);
  };

  if (loading) return <LoadingSpinner />;

  // Submitting/completed overlay
  if (submitting || completed) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#FAF8F6',
          gap: 3,
        }}
      >
        {completed ? (
          <>
            <Typography
              sx={{
                fontFamily: "'Carattere', cursive",
                fontSize: '2.5rem',
                color: '#B07A8F',
                textAlign: 'center',
              }}
            >
              Your wedding is all set!
            </Typography>
          </>
        ) : (
          <>
            <CircularProgress sx={{ color: '#B07A8F' }} />
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              Setting up your wedding...
            </Typography>
          </>
        )}
      </Box>
    );
  }

  const isLastMobileTopic = isMobile && activeStep === 3 && mobileTopicIndex >= activeTopics.length - 1;
  const showFinish = activeStep === 3 && (!isMobile || isLastMobileTopic);
  const showSkip = activeStep > 0;
  const nextDisabled = activeStep === 0 && !weddingTitle.trim();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#FAF8F6',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: { xs: 2, sm: 4 },
        px: 2,
      }}
    >
      <Typography
        sx={{
          fontFamily: "'Carattere', cursive",
          fontSize: { xs: '2rem', sm: '2.5rem' },
          color: '#B07A8F',
          mb: 3,
          textAlign: 'center',
        }}
      >
        Wedding CC
      </Typography>

      <Card
        sx={{
          maxWidth: 700,
          width: '100%',
          borderRadius: 3,
          p: { xs: 2, sm: 3 },
          overflow: 'visible',
        }}
      >
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step content */}
        <Box sx={{ minHeight: { xs: 'auto', sm: 300 } }}>
          {activeStep === 0 && (
            <EventDetailsStep
              title={weddingTitle}
              date={weddingDate}
              venue={weddingVenue}
              onChange={handleEventChange}
            />
          )}
          {activeStep === 1 && (
            <PlannersStep
              planners={planners}
              onChange={setPlanners}
              onPendingNameChange={setPendingPlannerName}
              addPendingRef={addPendingPlannerRef}
            />
          )}
          {activeStep === 2 && (
            <TopicSelectionStep
              suggestions={suggestions}
              selectedTopics={selectedTopics}
              onChange={handleTopicsChange}
            />
          )}
          {activeStep === 3 && (
            <TaskSelectionStep
              suggestions={suggestions}
              selectedTopics={selectedTopics}
              selectedTasks={selectedTasks}
              onChange={setSelectedTasks}
              mobileTopicIndex={mobileTopicIndex}
            />
          )}
        </Box>

        {/* Navigation */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 3,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            sx={{ color: 'grey.600' }}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {showSkip && (
              <Button onClick={handleSkip} sx={{ color: 'grey.500' }}>
                Skip
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={nextDisabled}
            >
              {showFinish ? 'Finish' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Planner name prompt dialog */}
      <Dialog open={showPlannerPrompt} onClose={() => setShowPlannerPrompt(false)}>
        <DialogTitle>Add planner?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You typed "{pendingPlannerName}" but didn't add it. Would you like to add this planner before continuing?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowPlannerPrompt(false); setActiveStep(2); }} sx={{ color: 'grey.600' }}>
            Skip
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              addPendingPlannerRef.current?.();
              setShowPlannerPrompt(false);
              setActiveStep(2);
            }}
          >
            Add & Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OnboardingPage;
